import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Breadcrumbs, type BreadcrumbItemData } from './Breadcrumbs';
import { useBreadcrumbs } from './use-breadcrumbs';

/**
 * W16-F4-S3 — page-shell breadcrumb container.
 *
 * Renders `<Breadcrumbs>` under the topbar when the route stack produces
 * 2 or more items. Detail pages register dynamic label overrides via
 * `useBreadcrumbOverride()` so a route handle's fallback label
 * ("Building #123") flips to the resolved name once react-query data
 * arrives.
 */

type OverrideMap = Record<string, string>;

interface BreadcrumbOverrideContextValue {
  overrides: OverrideMap;
  setOverride: (path: string, label: string) => void;
  clearOverride: (path: string) => void;
}

const BreadcrumbOverrideContext =
  createContext<BreadcrumbOverrideContextValue | null>(null);

export function BreadcrumbOverrideProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const value = useMemo<BreadcrumbOverrideContextValue>(
    () => ({
      overrides,
      setOverride: (path, label) =>
        setOverrides((prev) =>
          prev[path] === label ? prev : { ...prev, [path]: label },
        ),
      clearOverride: (path) =>
        setOverrides((prev) => {
          if (!(path in prev)) return prev;
          const next = { ...prev };
          delete next[path];
          return next;
        }),
    }),
    [overrides],
  );
  return (
    <BreadcrumbOverrideContext.Provider value={value}>
      {children}
    </BreadcrumbOverrideContext.Provider>
  );
}

/**
 * Detail-page hook: register a dynamic label for the current route's
 * pathname (e.g. building name from react-query). Pass `null` while data
 * is unresolved — the trail keeps the fallback from the route handle.
 *
 * Cleans up on unmount so the override doesn't leak across navigations.
 */
export function useBreadcrumbOverride(
  pathname: string,
  label: string | null | undefined,
): void {
  const ctx = useContext(BreadcrumbOverrideContext);
  useEffect(() => {
    if (!ctx) return;
    if (label) {
      ctx.setOverride(pathname, label);
    }
    return () => {
      ctx.clearOverride(pathname);
    };
    // We intentionally include only `pathname` + `label` so the override
    // re-applies when either changes; ctx is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, label]);
}

/** Apply registered overrides on top of route-handle-resolved items. */
function applyOverrides(
  items: readonly BreadcrumbItemData[],
  overrides: OverrideMap,
): BreadcrumbItemData[] {
  return items.map((item) => {
    if (item.path && overrides[item.path]) {
      return { ...item, label: overrides[item.path]! };
    }
    return { ...item };
  });
}

export function BreadcrumbBar(): JSX.Element | null {
  const items = useBreadcrumbs();
  const ctx = useContext(BreadcrumbOverrideContext);
  const overrides = ctx?.overrides ?? {};
  // The last item has no `to`; its override key would be the route path
  // we *would* have linked it to. Look up via the active path from items
  // when the last item has no override match — fall back to original.
  const merged = applyOverrides(items, overrides);

  if (merged.length < 2) return null;

  return (
    <div className="border-b border-border bg-background px-4 py-3 lg:px-8">
      <Breadcrumbs items={merged} className="text-sm text-muted-foreground" />
    </div>
  );
}
